import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const StartingAndFindingEPCRs = () => (
  <Page
    title="Starting and Finding ePCRs"
    intro="The ePCR dashboard is the entry point for starting new electronic patient care records and returning to records that are already in progress."
  >
    <Section title="The ePCR dashboard">
      <Bullets>
        <Bullet>
          Open <DocLink url="/epcr" text="ePCR Dashboard" /> to see counts of
          your in-progress drafts and recent ePCRs, and to start a new record.
        </Bullet>
        <Bullet>
          <strong>ePCR's in Progress</strong> lists your current drafts —
          records you created (and have not handed over) or that are currently
          owned by you — so you can resume one instead of starting again.
        </Bullet>
        <Bullet>
          <strong>My Recent ePCR's</strong> opens{' '}
          <DocLink url="/epcr/recents" text="My Recent ePCRs" /> — records you
          submitted, or handed to a treatment location, within the last 24
          hours. Opening a record from Recents is recorded in its access log.
        </Bullet>
      </Bullets>
      <Info title="Who can do this?">
        Any signed-in user can start an ePCR and see their own drafts and
        recents. Searching and reviewing other people's records is a
        governance function — see{' '}
        <DocLink
          docsPage
          url="/clinical-records/reviewing-and-managing"
          text="Reviewing and Managing Patient Report Forms"
        />
        .
      </Info>
    </Section>

    <Section title="Record types">
      <p>
        Starting a new record opens a record-type popup where you choose
        between the record types your organisation has enabled — full ePCR,
        Fast Track, Welfare, and Critical Care. You can change the type at any
        time before submission — from the <strong>Record Type</strong> button
        on the ePCR sidebar (or the header switch icon on Fast Track and
        Welfare records).
      </p>
      <Screenshot url="patient-records/epcr-new-record.png">
        The record-type popup shown when starting a new record, offering the
        types your organisation has enabled.
      </Screenshot>
      <Bullets>
        <Bullet>
          A <strong>full ePCR</strong> works through the sidebar modules:
          Incident, Demographics, PMHx, History, Assess, Vitals, Treatment,
          Attachments, Plan, Sharing, and Review.
        </Bullet>
        <Bullet>
          <strong>Fast Track</strong> is a single shorter form for low-acuity,
          advice-only, or no-treatment contacts, covering incident details
          (including any health and safety concern), demographics, history, a
          reduced treatment set (Medicines and Wound Closure), a plan,
          response times, and attachments.
        </Bullet>
        <Bullet>
          <strong>Welfare</strong> is a basic non-clinical record for lost or
          found children, safeguarding alerts passed from another
          organisation, lost or found property, welfare checks, and similar
          event welfare matters. It captures a category, what happened,
          actions taken, an outcome, and optional details of the person
          involved.
        </Bullet>
        <Bullet>
          <strong>Critical Care</strong> is for HEMS and pre-hospital critical
          care teams. It is the full ePCR plus live tap-to-log consoles for
          arrest, trauma, emergency anaesthesia and more — opened from the{' '}
          <strong>Live console</strong> tab at the top of the sidebar, with a
          separate <strong>Cardiac arrest</strong> console once an arrest is
          logged. Console taps are logged with one touch and a badge on the
          sidebar counts the entries still waiting for their details to be
          filled in afterwards.
        </Bullet>
      </Bullets>
      <Info title="Record types are enabled per organisation">
        Which types appear in the popup is set in{' '}
        <strong>Site Configuration → Patient Records</strong>. New
        organisations start with ePCR, Fast Track, and Welfare; the full ePCR
        is always available and cannot be switched off.
      </Info>
      <Info title="Event incidents: did it occur at the event?">
        When the incident type is <strong>Event</strong>, the Incident step
        also asks <strong>Did it occur at the event?</strong> (Yes / No /
        Unknown) on full and Fast Track records. Answer it: the post-event
        analysis uses it to separate injuries and illnesses caused at the
        event from patients who arrived with something that happened
        elsewhere.
      </Info>
      <Info title="Choosing a record type">
        Fast Track is intended for minor injuries, advice-only contacts, and
        similar low-acuity presentations; Welfare is for non-clinical matters
        with no patient assessment at all. Use a full ePCR whenever a patient
        has received meaningful clinical assessment or treatment — and always
        when there could be safeguarding concerns, because the safeguarding
        questions are part of the full ePCR and Critical Care records only.
      </Info>
    </Section>

    <Section title="Linking a CAD incident">
      <p>
        When your organisation runs the Command &amp; Control module, the{' '}
        <strong>Incident Number</strong> field on the Incident step links the
        record to its CAD incident. Typing a 10-digit incident number
        triggers a lookup: a confirmation popup shows the matched incident,
        and confirming it fills and locks the incident fields from CAD — the
        incident type, linked event, location, who reported it, and the
        reported complaint.
      </p>
      <Bullets>
        <Bullet>
          If it is not the right incident, choose not to link it and nothing
          changes — the same number will not prompt again until it is edited.
        </Bullet>
        <Bullet>
          Editing the number away from a linked incident removes the link, so
          a mistyped number cannot leave stale CAD details on the record.
        </Bullet>
        <Bullet>
          The lookup needs a connection; numbers that do not match any CAD
          incident are flagged so you can check and retype.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Correcting medicines on a draft">
      <p>
        A tracked medicine administration can be edited or removed while the
        record is still in Draft. An edit creates a linked correction record;
        a removal voids the administration and returns the stock to the drug
        bag — the original log is kept either way, and controlled-drug
        changes need a witness just like the original administration. Once
        the record is submitted, medicine entries are fixed.
      </p>
    </Section>

    <Section title="Pre-filling patient details from an event's entry list">
      <p>
        When the record&apos;s linked event has a participant list uploaded
        (see{' '}
        <DocLink
          docsPage
          url="/event-planning/shifts-and-documents"
          text="Shifts and Documents"
        />
        ), a <strong>Participant Lookup</strong> field appears at the top of
        the Patient Details step on full ePCR, Fast Track, and Critical Care
        records — on both the web and mobile apps.
      </p>
      <Step number={1} title="Search by bib number">
        Type the patient&apos;s bib or race number. The match is exact —
        &quot;007&quot; and &quot;7&quot; are different numbers — so enter it
        as printed.
      </Step>
      <Step number={2} title="Confirm the match">
        A confirmation popup shows the participant&apos;s details first —
        name, date of birth and age, club, contact and emergency contact, and
        any medical notes from the entry list.{' '}
        <strong>Participants sometimes swap bib numbers</strong>, so check the
        details against the patient in front of you before choosing{' '}
        <strong>Use These Details</strong>. If it is not them, choose{' '}
        <strong>Not This Person</strong> and nothing is changed.
      </Step>
      <Step number={3} title="Review the filled fields">
        Confirming fills the patient&apos;s name, date of birth, gender,
        address, contact number, and next of kin (from the emergency contact),
        and records the bib number on the record. Every field stays editable —
        correct anything the entry list got wrong. Medical notes, allergies,
        and medications from the list are shown in the popup for reference
        only; record the history you take from the patient as normal.
      </Step>
      <Info title="Mobile needs a connection for the search">
        On the mobile app the lookup searches the server&apos;s copy of the
        list, so it needs an internet connection — the rest of the record
        still works offline as usual.
      </Info>
    </Section>

    <Section title="Submitting and handing over">
      <Step number={1} title="Complete the Review step">
        The Review step checks required fields before submission. Every record
        type needs an incident type (with the linked event where it is an
        Event incident) and an <strong>Incident Clear Time</strong>. Clinical
        records (ePCR, Fast Track, and Critical Care) also need patient
        surname and date of birth (or their &quot;unknown&quot; flags) and a
        patient outcome; full ePCR and Critical Care records add patient-side
        time and an answer to the safeguarding question; and when the patient
        has consented to the feedback SMS, the phone number it will go to
        (Critical Care records do not send the feedback SMS). Welfare records
        only require a category, details of what happened, and an outcome.
      </Step>
      <Step number={2} title="Submit, or hand the record over">
        Instead of submitting, the owning clinician can transfer the record to
        a colleague (it returns to their drafts), or hand the patient over to
        an event treatment location. Every transfer stores a snapshot of the
        record at the point of handover, and the record&apos;s header keeps
        the full history — who created it, every change of owner with when
        and why, and who holds it now.
      </Step>
      <Step number={3} title="Return to it from Recents">
        Submitted and handed-over records stay in My Recent ePCRs for 24
        hours. After that, they are found through Patient Report Forms by
        governance staff.
      </Step>
      <Info title="Deleting a draft started by mistake">
        Crews can delete their own draft only while it holds no patient
        identity — no patient name or date of birth (or their &quot;unknown&quot;
        flags) and no live-console events. Once any of those exist the record
        is a clinical record, and only a manager can delete it through{' '}
        <DocLink
          docsPage
          url="/clinical-records/reviewing-and-managing"
          text="Patient Report Forms"
        />
        .
      </Info>
    </Section>

    <Section title="Practice mode">
      <p>
        The dashboard also offers a <strong>Practice ePCR</strong> (behind a
        confirmation prompt). Practice records cannot be saved and must not be
        used for real patients — they exist so staff can learn the ePCR layout
        without creating a real clinical record.
      </p>
      <Warning title="Not for real patients">
        Practice ePCRs are explicitly unsaveable. Always start a real ePCR or
        Fast Track ePCR from the dashboard when treating an actual patient.
        Practice mode is only available on the web app, not on mobile.
      </Warning>
    </Section>
  </Page>
)

export default StartingAndFindingEPCRs
